package top.wcpe.allincore.ui.runtime.ir.call

import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import top.wcpe.allincore.ui.runtime.ir.DslValueUtils
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.reactive.ReactiveDebug
import kotlin.jvm.JvmStatic

/**
 * `debug.recompose("tag")` / `debug.deps()` 反应式内省内建的运行时实现。
 *
 * 两者都需在"追踪上下文"（即某个 Effect / Computed 求值期间，例如元素属性绑定的重算）
 * 内调用才有意义；在 action / fun body 等非追踪上下文调用时给出降级返回值并打提示日志。
 */
class DebugApiInvoker private constructor() {

    companion object {
        private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.call.DebugApiInvoker")

        @JvmStatic
        fun invoke(method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? = when (method) {
            "recompose" -> recompose(argNodes, ctx)
            "deps" -> deps(argNodes, ctx)
            else -> throw DslRuntimeError("DebugApi 不支持的方法: $method")
        }

        /**
         * `debug.recompose("tag")`：返回当前 Effect 重组序号（double），并打中文 INFO 日志。
         * 不在 Effect 上下文时返回 0。
         */
        private fun recompose(argNodes: List<ExprNode>, ctx: EvalContext): Any? {
            val tag = if (argNodes.isEmpty()) "" else stringArg("debug.recompose()", argNodes, 0, ctx)
            val count = ReactiveDebug.currentRecomposeCount()
            if (count < 0) {
                LOGGER.info("【重组】$tag：不在 Effect 上下文（返回 0）")
                return 0.0
            }
            LOGGER.info("【重组】$tag 第 $count 次")
            return count.toDouble()
        }

        /**
         * `debug.deps()`：返回当前 Effect 追踪到的依赖名字列表（List<String>），并打中文 INFO 日志。
         *
         * 名字来源：反查 `state` 与 `derived` 表中与依赖实例身份相同的字段名；
         * 无法命名的反应式集合（ReactiveList / ReactiveMap 内部 Trackable）按类型标签兜底。
         * 不在追踪上下文时返回空列表。
         */
        @Suppress("UnusedParameter")
        private fun deps(nodes: List<ExprNode>, ctx: EvalContext): Any? {
            if (!ReactiveDebug.inTrackingContext()) {
                LOGGER.info("【依赖】不在 Effect 上下文（返回空列表）")
                return mutableListOf<String>()
            }
            val names = mutableListOf<String>()
            // state 字段反查
            for ((key, value) in ctx.states) {
                if (ReactiveDebug.isTracked(value)) {
                    names.add(key)
                }
            }
            // derived 字段反查（加 derived. 前缀以区分命名空间）
            for ((key, value) in ctx.derived) {
                if (ReactiveDebug.isTracked(value)) {
                    names.add("derived.$key")
                }
            }
            val total = ReactiveDebug.currentDepsCount()
            // 命名外的依赖（反应式集合等）以类型标签兜底，保证计数与列表一致。
            val unnamed = total - names.size
            for (i in 0 until unnamed) {
                names.add("<reactive>")
            }
            LOGGER.info("【依赖】当前追踪 ${names.size} 个：$names")
            return names
        }

        private fun stringArg(name: String, argNodes: List<ExprNode>, index: Int, ctx: EvalContext): String {
            val value = ExprEvaluator.eval(argNodes[index], ctx)
            if (value !is String) {
                throw DslRuntimeError("$name 第 ${index + 1} 个参数必须为 String，实际类型: ${DslValueUtils.classOf(value)}")
            }
            return value
        }
    }
}
